import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import { Menu, MenuItem } from '@mui/material';
import { useDispatch } from 'react-redux';
import PopupTitle from '../common/popup_title';
import {
  END_USER_RECIPE_COLLECTIONS_ROUTE,
  END_USER_RECIPE_COLLECTION_EDIT_COVER_IMAGE_ROUTE,
  END_USER_RECIPE_COLLECTION_EDIT_STRUCTURE_ROUTE,
} from '../../services/routes';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import { removeRecipesBoard } from '../../operations/recipes_operations';
import {
  useDeleteMealMutation,
  useDeleteSmorgBoardMutation,
  useGetSharedBoardQuery,
  useGetSmorgBoardQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';
import {
  mealFromSharedMeal,
  mealWithScaledIngredientsReplaced,
} from '../../services/meals';
import { scaleMealIngredientsOperation } from '../../operations/meal_operations';
import { mealsAddedToMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { useGetCurrentSpaceMembershipQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { ServingsModal } from '../../features/end_user_shopping/components/servings_modal';
import TooManyMealsErrorModal from '../../features/end_user_shopping/components/too_many_meals_error_modal';
import { MAX_MEALS_TO_ADD_TO_BASKET_AT_ONCE } from '../../features/end_user_shopping/services/shopping_lists';

const RecipeCollectionBoardActionsMenu = ({
  boardID,
  isSharedBoard,
  isReadOnly,
  anchorEl,
  onDismiss,
  onClickCreateNewBoard,
  onClickRenameBoard,
  deleteBoardDisabled,
  renameBoardDisabled,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const [servingsModalAnchorEl, setServingsModalAnchorEl] = useState(null);

  const { data: ownRecipesBoard } = useGetSmorgBoardQuery(boardID, {
    skip: !boardID || isSharedBoard,
  });

  const { data: sharedRecipesBoard } = useGetSharedBoardQuery(boardID, {
    skip: !boardID || !isSharedBoard,
    refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
  });

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteSmorgBoard] = useDeleteSmorgBoardMutation();

  const [updateSpace] = useUpdateSpaceMutation();

  const [deletePopupAnchorEl, setDeletePopupAnchorEl] = useState(null);

  const [isAddingMealsToBasket, setIsAddingMealsToBasket] = useState(false);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const mealIDsToAddToBasket = (sharedRecipesBoard || ownRecipesBoard).menus
    .flatMap((menu) => menu.sharedMealIDs || menu.mealIDs)
    .filter(Boolean);

  const handleClickAddAllMealsToBasket = (ev) => {
    if (mealIDsToAddToBasket.length > MAX_MEALS_TO_ADD_TO_BASKET_AT_ONCE) {
      setServingsModalAnchorEl(null);
      setIsAddingMealsToBasket(false);
      setIsAddingTooManyMeals(true);
      setServingsModalAnchorEl(null);
      return;
    }
    setServingsModalAnchorEl(ev.currentTarget);
  };

  const handleAddAllMealsToBasket = async (scaleToServings) => {
    setIsAddingMealsToBasket(true);
    try {
      const fullMealsToAddToBasket = (
        await Promise.all(
          mealIDsToAddToBasket.map(async (mealID) => {
            let fullMeal;
            if (isSharedBoard) {
              const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
              fullMeal = mealFromSharedMeal(fullSharedMeal);
              fullMeal.id = mealID;
            } else {
              const { data } = await lazyGetMeal(mealID);
              fullMeal = data;
            }
            return fullMeal;
          }),
        )
      ).filter(Boolean);
      const scaledMealsToAddToBasket = await Promise.all(
        fullMealsToAddToBasket.map(async (fullMeal) => {
          const scaleFactor =
            scaleToServings / (fullMeal.recipes[0]?.servings || 1);
          const scaledIngredients = await scaleMealIngredientsOperation(
            fullMeal,
            scaleFactor,
            customerFoodDataID,
          );
          return mealWithScaledIngredientsReplaced(
            fullMeal,
            scaleToServings,
            scaledIngredients,
            scaleFactor,
          );
        }),
      );
      dispatch(mealsAddedToMealBasket(scaledMealsToAddToBasket));
      onDismiss();
    } finally {
      setServingsModalAnchorEl(null);
      setIsAddingMealsToBasket(false);
    }
  };

  const handleClickDeleteBoard = (ev) => {
    setDeletePopupAnchorEl(ev.target);
  };

  const handleDelete = () => {
    removeRecipesBoard(
      ownRecipesBoard,
      null,
      deleteMeal,
      deleteSmorgBoard,
      updateSpace,
      dispatch,
    );
    onDismiss();
    navigate(END_USER_RECIPE_COLLECTIONS_ROUTE);
  };

  const handleEditBoardStructure = () => {
    navigate(
      formatRoute(END_USER_RECIPE_COLLECTION_EDIT_STRUCTURE_ROUTE, {
        boardID,
      }),
    );
  };

  const handleEditCoverImage = () => {
    navigate(
      formatRoute(END_USER_RECIPE_COLLECTION_EDIT_COVER_IMAGE_ROUTE, {
        boardID,
      }),
    );
  };

  const [isAddingTooManyMeals, setIsAddingTooManyMeals] = useState(false);

  const onDismissTooManyMealsErrorModal = () => {
    setIsAddingTooManyMeals(false);
    onDismiss();
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open onClose={onDismiss}>
        <PopupTitle
          titleText="Board actions"
          closeButtonEnabled
          onClickClose={onDismiss}
        />
        <MenuItem onClick={onClickCreateNewBoard}>Create new board</MenuItem>
        <MenuItem onClick={handleClickAddAllMealsToBasket}>
          Add all meals to basket
        </MenuItem>
        {!isReadOnly && (
          <>
            <MenuItem
              onClick={onClickRenameBoard}
              disabled={renameBoardDisabled}>
              Rename board
            </MenuItem>
            <MenuItem onClick={handleEditBoardStructure}>
              Edit board structure
            </MenuItem>
            <MenuItem onClick={handleEditCoverImage}>Edit cover image</MenuItem>
            <MenuItem
              onClick={handleClickDeleteBoard}
              disabled={deleteBoardDisabled}>
              Delete board
            </MenuItem>
          </>
        )}
      </Menu>
      <DeleteCardConfirmPopover
        anchorEl={deletePopupAnchorEl}
        visible={!!deletePopupAnchorEl}
        onDismiss={() => setDeletePopupAnchorEl(null)}
        message="The board and all its meals will be deleted. Are you sure?"
        onConfirmDelete={handleDelete}
        height={160}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        title="Delete board"
      />
      {servingsModalAnchorEl && (
        <ServingsModal
          anchorEl={servingsModalAnchorEl}
          isLoading={isAddingMealsToBasket}
          onSubmit={(scaleToServings) => {
            handleAddAllMealsToBasket(scaleToServings);
          }}
          onDismiss={() => setServingsModalAnchorEl(null)}
        />
      )}
      {isAddingTooManyMeals && (
        <TooManyMealsErrorModal onDismiss={onDismissTooManyMealsErrorModal} />
      )}
    </>
  );
};

RecipeCollectionBoardActionsMenu.propTypes = {
  boardID: PropTypes.string.isRequired,
  isSharedBoard: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  anchorEl: PropTypes.element.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onClickCreateNewBoard: PropTypes.func.isRequired,
  onClickRenameBoard: PropTypes.func.isRequired,
  deleteBoardDisabled: PropTypes.bool.isRequired,
  renameBoardDisabled: PropTypes.bool.isRequired,
};

export default RecipeCollectionBoardActionsMenu;
